import { Navigate, Outlet, useLocation } from 'react-router-dom';
import FullLayout from '../layouts/FullLayout';
import { useAuth } from '../components/context/AuthContext';

/**
 * PrivateRoute
 * 
 * Protege las rutas internas de la aplicación (por ejemplo /app/starter)
 * asegurando que solo sean accesibles si:
 *  - Existe una sesión activa (auth.idSesion)
 *  - Se ha completado la verificación biométrica (auth.bioOk)
 *  - Y, opcionalmente, la ventana de validez biométrica (auth.bioExp) no ha expirado
 * 
 * Si no se cumplen estas condiciones, redirige automáticamente a /auth/login.
 */

const DESARROLLO_SIN_LOGIN = false; // Cambiar a true solo si se quiere desactivar la seguridad en desarrollo

const PrivateRoute = () => {
  const { authReady } = useAuth();
  const location = useLocation();

  // Recuperar datos de sesión desde localStorage
  const idSesion = localStorage.getItem('auth.idSesion');
  const bioOk = localStorage.getItem('auth.bioOk') === 'true';
  const bioExp = Number(localStorage.getItem('auth.bioExp') || 0);
  const now = Date.now();

  // Verifica si la ventana biométrica sigue vigente
  const bioWindowVigente = !bioExp || now < bioExp;

  // Condición principal: sesión válida y biometría aprobada
  const sesionValida = Boolean(idSesion && bioOk && bioWindowVigente);

  // 1) Mientras la autenticación inicial no haya terminado, no renderizamos nada
  // Esto evita parpadeos o errores antes de saber el estado real de la sesión
  if (!authReady) {
    return null; // También se puede colocar un spinner de carga si se prefiere
  }

  // 2) Permitir acceso libre durante el desarrollo, si se requiere probar sin login
  if (DESARROLLO_SIN_LOGIN) {
    return (
      <FullLayout>
        <Outlet />
      </FullLayout>
    );
  }

  // 3) Si la sesión es válida, se permite el acceso a las rutas hijas protegidas
  if (sesionValida) {
    return (
      <FullLayout>
        <Outlet />
      </FullLayout>
    );
  }

  // 4) Si no hay sesión válida, limpiamos posibles residuos para evitar falsos positivos
  localStorage.removeItem('auth.idSesion');
  localStorage.removeItem('auth.hashId');
  localStorage.removeItem('auth.cedula');
  localStorage.removeItem('auth.bioOk');
  localStorage.removeItem('auth.bioExp');

  // 5) Redirigir al login, conservando la ruta de origen en el estado
  // Esto permite, si se desea más adelante, mostrar mensajes personalizados
  return <Navigate to="/auth/login" replace state={{ from: location.pathname }} />;
};

export default PrivateRoute;
